import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Layers, Plus, CheckCircle2, AlertTriangle, FileCheck } from 'lucide-react';
import type { LedgerEntry } from '../../../../../shared/types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost/fhpf-api';

export const LandPayments: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [landPayments, setLandPayments] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Form State
  const [showAddModal, setShowAddModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [sellerName, setSellerName] = useState('');
  const [deedRef, setDeedRef] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'ledger'),
      where('type', '==', 'land_payment'),
      orderBy('sequenceNumber', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: LedgerEntry[] = [];
        snap.forEach((doc) => list.push(doc.data() as LedgerEntry));
        setLandPayments(list);
        setLoading(false);
      },
      (err) => {
        console.warn('[FHPF] Land payments load error:', err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const numAmount = parseInt(amount, 10);
    if (!numAmount || numAmount <= 0) {
      alert('Amount must be a positive integer in BDT.');
      return;
    }

    if (sellerName.trim().length < 2) {
      alert('Seller/Recipient name is required.');
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/land-payments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: numAmount,
          sellerName: sellerName.trim(),
          deedRef: deedRef.trim(),
          notes: notes.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.message || 'Failed to record land payment.');
      }

      setMessage({ type: 'success', text: `Land acquisition payment recorded at Ledger sequence #${data.sequenceNumber}.` });
      setShowAddModal(false);
      setAmount('');
      setSellerName('');
      setDeedRef('');
      setNotes('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to record land payment.' });
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n: number) => `৳${n.toLocaleString('en-US')}`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Layers className="w-5 h-5 text-primary-500" />
            <span>Land Acquisition Payments</span>
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Records disbursements towards the Manikganj / Savar parcel acquisition.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold flex items-center gap-2 shadow-sm shadow-primary-600/20"
        >
          <Plus className="w-4 h-4" />
          <span>{t('admin.addLandPayment')}</span>
        </button>
      </div>

      {message && (
        <div
          className={`p-4 rounded-xl border flex items-start gap-2.5 text-xs sm:text-sm ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900'
              : 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* List */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : landPayments.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">No land payments recorded yet.</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-navy-800">
            {landPayments.map((p) => (
              <div key={p.entryId} className="p-4 sm:p-5 flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-gray-500">#{p.sequenceNumber}</span>
                    <span className="font-semibold text-sm text-gray-900 dark:text-white">{p.note}</span>
                  </div>
                  <div className="text-xs text-gray-400 flex items-center gap-3">
                    <span>{p.createdAt}</span>
                    <span>Deed/Ref: {p.refId}</span>
                    <span className="font-mono text-[10px]">Hash: {p.entryHash.substring(0, 10)}...</span>
                  </div>
                </div>

                <div className="text-right">
                  <span className="font-bold text-base text-primary-600 dark:text-primary-400">
                    {fmt(p.amount)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-navy-900 rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200 dark:border-navy-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Record Land Acquisition Payment
            </h3>

            <form onSubmit={handleAddPayment} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Payment Amount (BDT)
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 250000"
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Seller or Recipient Name
                </label>
                <input
                  type="text"
                  required
                  value={sellerName}
                  onChange={(e) => setSellerName(e.target.value)}
                  placeholder="Full name of seller / registry office"
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Deed / Registry Reference #
                </label>
                <input
                  type="text"
                  value={deedRef}
                  onChange={(e) => setDeedRef(e.target.value)}
                  placeholder="e.g. DEED-SAVAR-00412"
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Notes
                </label>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Installment number, land parcel details..."
                  className="w-full p-2.5 text-sm rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 outline-none text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-xs font-semibold rounded-xl border border-gray-200 dark:border-navy-700 text-gray-600 dark:text-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-xs font-semibold rounded-xl bg-primary-600 hover:bg-primary-700 text-white"
                >
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
